import React, { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import styled from "@emotion/styled";
import { colors } from "@utils/ui_palette";
import { rgba } from "emotion-rgba";
import { useSelector } from "react-redux";
import { RootState } from "../store/reducer";
import Login from "./Login";
import AlertModal, { Alerts } from "@components/common/AlertModal";
import axios, { AxiosError } from "axios";
import { useAppDispatch } from "../store";
import userSlice from "../slices/user";
import { RotatingLines } from "react-loader-spinner";
import {
  IoHomeOutline,
  IoPeopleOutline,
  IoLibraryOutline,
  IoMicOutline,
  IoMailOpenOutline,
  IoEnterOutline,
  IoPersonSharp,
} from "react-icons/io5";
import { useNavigate } from "react-router-dom";

function Layouts() {
  const dispatch = useAppDispatch();
  const navigation = useNavigate();
  const isLoggedIn = useSelector(
    (state: RootState) => !!state.user.userInfo.accessToken
  );
  const user = useSelector((state: RootState) => state.user.userInfo);
  const [bodyLoading, setBodyLoading] = useState<boolean>(true);

  useEffect(() => {
    const getTokenAndRefresh = async () => {
      const token = localStorage.getItem("refreshToken");
      setBodyLoading(true);

      if (!token) {
        Alerts.show({
          title: "Please login again.",
        });
        setBodyLoading(false);
        return;
      }

      try {
        const { data } = await axios.post(
          `${process.env.REACT_APP_API}/auth/refresh-token`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        dispatch(
          userSlice.actions.setUserInfo({
            ...data.data.user,
            accessToken: data.data.accessToken,
          })
        );

        localStorage.setItem("refreshToken", data.data.refreshToken);
      } catch (error) {
        const errorResponse = error as AxiosError as any;
        if (errorResponse.data.code === "expired") {
          Alerts.show({
            title: "Please login again.",
          });
        }
      } finally {
        setBodyLoading(false);
      }
    };
    getTokenAndRefresh();
  }, [dispatch, setBodyLoading]);

  useEffect(() => {
    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const {
          config,
          response: { status },
        } = error;
        if (status === 419) {
          if (error.response.data.code === "expired") {
            console.log("hihihi");
            const originalRequest = config;
            const refreshToken = localStorage.getItem("refreshToken");
            // token refresh 요청
            const { data } = await axios.post(
              `${process.env.API_URL}/auth/refresh-token`, // token refresh api
              {},
              { headers: { authorization: `Bearer ${refreshToken}` } }
            );
            // 새로운 토큰 저장
            dispatch(userSlice.actions.setAccessToken(data.data.accessToken));
            originalRequest.headers.authorization = `Bearer ${data.data.accessToken}`;
            // 419로 요청 실패했던 요청 새로운 토큰으로 재요청
            return axios(originalRequest);
          }
        }
        if (status === 418) {
          localStorage.removeItem("refreshToken");
          Alerts.show({
            title: "Please login again.",
          });
        }
        return Promise.reject(error);
      }
    );
  }, [dispatch]);
  return (
    <Container>
      {bodyLoading ? (
        <LoadingBox>
          <RotatingLines visible={true} strokeColor={colors.Primary50} />
        </LoadingBox>
      ) : (
        <ContentBody>
          {isLoggedIn ? (
            <>
              <EmptyBox />
              <MenuBox>
                <LogoBox>
                  <Logo src="/img/img_login_logo.svg" />
                </LogoBox>

                <MenuListGroup>
                  <MenuListItem onClick={() => navigation("/")}>
                    <MenuText>
                      <IoHomeOutline size={20} stroke={colors.White} />
                      <span>Main</span>
                    </MenuText>
                  </MenuListItem>
                  <MenuListItem onClick={() => navigation("/users")}>
                    <MenuText>
                      <IoPeopleOutline size={25} stroke={colors.White} />
                      <span>Users</span>
                    </MenuText>
                  </MenuListItem>
                  <MenuListItem onClick={() => navigation("/assessment")}>
                    <MenuText>
                      <IoLibraryOutline size={20} stroke={colors.White} />
                      <span>Assessments</span>
                    </MenuText>
                  </MenuListItem>
                  <MenuListItem>
                    <MenuText>
                      <IoMicOutline size={25} stroke={colors.White} />
                      <span>Speaking</span>
                    </MenuText>
                  </MenuListItem>
                  <MenuListItem onClick={() => navigation("/inquery-list")}>
                    <MenuText>
                      <IoMailOpenOutline size={20} stroke={colors.White} />
                      <span>Inquery Board</span>
                    </MenuText>
                  </MenuListItem>
                  <MenuListItem>
                    <MenuText>
                      <IoEnterOutline size={25} stroke={colors.White} />
                      <span>Logout</span>
                    </MenuText>
                  </MenuListItem>
                </MenuListGroup>
              </MenuBox>

              <Contents>
                <ContentsHeader>
                  <HeaderContainer>
                    <Mode>{process.env.REACT_APP_MODE} Server</Mode>
                    <UserInfo>
                      <IoPersonSharp size={20} />
                      <span>{user.username} logged in.</span>
                    </UserInfo>
                  </HeaderContainer>
                </ContentsHeader>
                <Outlet />
              </Contents>
            </>
          ) : (
            <Login />
          )}
        </ContentBody>
      )}

      <AlertModal />
    </Container>
  );
}

const HeaderContainer = styled.div`
  width: 1024px;
  height: 80px;
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 0 20px;
`;

const LoadingBox = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ContentsHeader = styled.div`
  width: 100%;
  height: 80px;
  background-color: ${colors.White};
  box-shadow: 10px 2px 10px ${rgba(colors.Gray50, 0.3)};
`;

const ContentBody = styled.div`
  display: flex;
  flex: 1;
`;

const Contents = styled.div`
  flex: 1;
`;

const EmptyBox = styled.div`
  flex-basis: 400px;
`;

const MenuBox = styled.div`
  position: fixed;
  width: 400px;
  height: 100vh;
  background: linear-gradient(360deg, #00fefe 0%, #124acc 100%);
  box-shadow: 10px 2px 10px ${rgba(colors.Gray50, 0.3)};
`;

const LogoBox = styled.div`
  padding: 30px;
  display: flex;
  align-items: center;
`;

const Logo = styled("img")`
  height: 50px;
`;

const MenuListGroup = styled.div``;

const MenuListItem = styled.div`
  cursor: pointer;
  padding: 0 30px;

  &:hover {
    background-color: ${rgba(colors.Black, 0.1)};
  }
`;

const MenuText = styled.div`
  border-bottom: 1px solid ${rgba(colors.White, 0.1)};
  padding: 30px 0;
  display: flex;
  align-content: center;

  span {
    margin-left: 10px;
    font-size: 18px;
    color: ${colors.White};
  }
`;

const Mode = styled.div`
  font-size: 18px;
  font-weight: 700;
  margin-right: 20px;
  color: ${colors.Primary50};
`;

const UserInfo = styled.div`
  font-size: 15px;
  display: flex;
  align-items: center;
  color: ${colors.Gray90};

  span {
    margin-left: 8px;
    color: ${colors.Gray90};
  }
`;

export default Layouts;
